
import React, {useState} from 'react';
import styles from './_generic-item-editor-dialog.module.scss';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@mui/material";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import {Input, InputChangeEvent} from "@progress/kendo-react-inputs";
import {Error, Hint} from "@progress/kendo-react-labels";
import {hasValue, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";
import {DialogProps} from "../../Dialogs/DialogProps";
import {startOfDay} from "../../../utils/LoanCalcUtils";

export interface IGenericItemFieldInfo {
  name: string;
  type: "Date" | "string" | "number";
  label: string;
  required?: boolean;
  readonly?: boolean;
  minDate?: Date;
}

export interface IGenericItemEditorError {
  message: string;
  canBeBypassed: boolean;
}

type SubmitButtonText = "Add" | "Update";
export type GenericEditorActionType = SubmitButtonText | "Bypass";

export interface GenericItemEditorDialogProps extends DialogProps<void> {
  item: any;
  editableFieldInfos: IGenericItemFieldInfo[];
  submitButtonText: SubmitButtonText;
  onRemove?: (id: string) => void;
  objectSpecificValidator?: (item: any, actionType: GenericEditorActionType) => IGenericItemEditorError[];
  onSubmit?: (item: any) => void;
}

export default function GenericItemEditorDialog(props: GenericItemEditorDialogProps) {
  const [item, setItem] = useState<typeof props.item>(props.item);
  const [objectSpecificValidatorErrors, setObjectSpecificValidatorErrors] = useState<IGenericItemEditorError[]>([]);
  const errorsThatCanBeBypassed = objectSpecificValidatorErrors.filter(e => e.canBeBypassed);
  const errorsThatCanNotBeBypassed = objectSpecificValidatorErrors.filter(e => !e.canBeBypassed);
  
  const updateItem = (fieldName: string, value: any) => {
    const updatedItem = {...item};
    updatedItem[fieldName] = value;
    setItem(updatedItem);
  }

  const onSubmit = (bypassTheErrorsThatCanBeBypassed: boolean, actionType: GenericEditorActionType) => {
    const errors: IGenericItemEditorError[] = requiredFieldsValidator();
    const dateErrors = datesValidator();
    errors.push(...dateErrors);
    
    if (props.objectSpecificValidator) {
      errors.push(...props.objectSpecificValidator(item, actionType));
    }

    setObjectSpecificValidatorErrors(errors);
    
    const thereAreErrorsThatCanNotBeBypassed = errors.filter(e => !e.canBeBypassed).length > 0;
    const thereAreErrorsThatCanBeBypassed = errors.filter(e => e.canBeBypassed).length > 0;

    // It returns early IF 
    // 1. There are any errors that CAN'T be bypassed
    // 2. There are errors that CAN be bypassed, but the user didn't click the "Bypass" button
    if (thereAreErrorsThatCanNotBeBypassed || (thereAreErrorsThatCanBeBypassed && !bypassTheErrorsThatCanBeBypassed)) {
      return;
    }

    if (props.onSubmit) {
      props.onSubmit(item);
    }
  }

  const fieldIsEmpty = (value: any, field: IGenericItemFieldInfo): boolean => {
    if (field.type === "Date") {
      return value === undefined || value === null;
    }

    if (field.type === "number") {
      return !hasValue(value) || Number.isNaN(value);
    }

    if (field.type === "string") {
      return !hasValue(value);
    }
    
      return false;
  }
  
  const requiredFieldsValidator = (): IGenericItemEditorError[] => {
    const emptyRequiredFields: string[] = [];
    const errors: IGenericItemEditorError[] = [];
    
    props.editableFieldInfos.forEach(field => {
      if (field.required && fieldIsEmpty(item[field.name], field)) {
        emptyRequiredFields.push(field.label);
      }
    })

    if (emptyRequiredFields.length > 0) {
      errors.push({message: 'The following fields are required-', canBeBypassed: false});
      emptyRequiredFields.forEach((field, index) => errors.push({message: `${index + 1}- ${field}`, canBeBypassed: false}));
    }
    
    return errors;
  }

  const datesValidator = (): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    props.editableFieldInfos.forEach(field => {
      if (field.type !== "Date" || !hasValue(item[field.name])) {
        return;
      }

      const value = new Date(item[field.name]);
      if (value.getFullYear() < 1000) {
        errors.push({message: "The payment date's year has to have 4 digits. Ex- instead of '23', enter '2023'.", canBeBypassed: false});
        return;
      }

      if (field.minDate && startOfDay(value).getTime() < startOfDay(field.minDate).getTime()) {
        errors.push({message: `${field.label} must be on or after ${field.minDate.toLocaleDateString()}.`, canBeBypassed: false});
      }
    })

    return errors;
  }
  
  return (
    <div className={styles.genericItemEditorDialogContainer}>
      <DialogTitle id="form-dialog-title">
        {props.title}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} className={styles.parentGrid}>
          <Grid item xs={12}>
            {props.editableFieldInfos.map((field, key) => {
              switch(field.type) {
                case "Date": {
                  return (
                    <Grid item xs={12} className={styles.mappedField} key={key}>
                      <DatePickerWithYearSanitizer
                        disabled={field.readonly}
                        label={field.label}
                        value={item[field.name] ? new Date(item[field.name]) : undefined}
                        min={field.minDate}
                        onChange={(event: DatePickerChangeEvent) => updateItem(field.name, event.value)}
                        required={field.required}
                      />
                    </Grid>
                  )
                }
                case "string": {
                  return (
                    <Grid item xs={12} className={styles.mappedField} key={key}>
                      <Input
                        readOnly={field.readonly}
                        label={field.label}
                        value={item[field.name]}
                        type='text'
                        onChange={(e: InputChangeEvent) => updateItem(field.name, e.target.value)}
                        required={field.required}
                      />
                    </Grid>
                  )
                }
                case "number": {
                  return (
                    <Grid item xs={12} className={styles.mappedField} key={key}>
                      <Input
                        readOnly={field.readonly}
                        label={field.label}
                        value={item[field.name]}
                        type='number'
                        onChange={(e: InputChangeEvent) => {
                          const value = Number.parseFloat(e.value);
                          updateItem(field.name, value);
                        }}
                        required={field.required}
                      />
                    </Grid>
                  )
                }
              }
            })}
            {errorsThatCanNotBeBypassed.length > 0 &&
              <Grid item xs={12}>
                {errorsThatCanNotBeBypassed.map((error, key) => <Error key={key}>{error.message}</Error>)}
              </Grid>
            }
            {errorsThatCanBeBypassed.length > 0 &&
              <Grid item xs={12}>
                {errorsThatCanBeBypassed.map((error, key) => <Hint key={key}>{error.message}</Hint>)}
              </Grid>
            }
          </Grid>
        </Grid>

        <DialogActions>
          {props.onSubmit && errorsThatCanBeBypassed.length > 0 && errorsThatCanNotBeBypassed.length === 0 &&
            <Button
              onClick={() => onSubmit(true, "Bypass")}
              color="error"
              variant="contained"
            >
              Bypass
            </Button>
          }
          {props.onSubmit &&
            <Button
              onClick={() => onSubmit(false, props.submitButtonText)}
              color="primary"
              variant="contained"
            >
              {props.submitButtonText}
            </Button>
          }
          {props.onRemove && item["id"] &&
            <Button
              onClick={() => props.onRemove!(item["id"])}
              color="error"
              variant="contained"
            >
              Remove
            </Button>
          }
          <Button
            autoFocus
            onClick={props.closeDialog}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </DialogContent>
    </div>
  );
}
